import type { ComponentType } from 'react';
import { Icon, IconButton } from '@aibees/shared/components';
import { buttonClasses, inputBaseClasses } from '@mui/material';
import type { IconButtonProps } from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';

const dateList = [
  'DatePicker',
  'DateTimePicker',
  'StaticDatePicker',
  'DesktopDatePicker',
  'DesktopDateTimePicker',
  //
  'MobileDatePicker',
  'MobileDateTimePicker',
];

const timeList = [
  'TimePicker',
  'MobileTimePicker',
  'StaticTimePicker',
  'DesktopTimePicker',
];

const switchIcon = (props: IconButtonProps) => (
  <IconButton
    onClick={props.onClick}
    size="xsmall"
    variant="text"
    icon="arrow-drop-down"
    iconSize={16}
  />
);

const leftIcon = (props: IconButtonProps) => (
  <IconButton
    onClick={props.onClick}
    size="xsmall"
    variant="outlined"
    icon="chevron-left"
    iconSize={16}
  />
);

const rightIcon = (props: IconButtonProps) => (
  <IconButton
    onClick={props.onClick}
    size="xsmall"
    variant="outlined"
    icon="chevron-right"
    iconSize={16}
  />
);

const calendarIcon = (props: IconButtonProps) => (
  <IconButton
    onClick={props.onClick}
    size="xsmall"
    variant="text"
    icon="calendar-today-outline"
    iconSize={16}
  />
);

const clockIcon = () => <Icon name="clock-fill" />;

type PickerComponentOverride = {
  defaultProps: {
    slots: Record<string, ComponentType<IconButtonProps>>;
    slotProps?: {
      textField: {
        InputLabelProps: { shrink: true };
      };
    };
  };
};

const desktopTypes = dateList.reduce<Record<string, PickerComponentOverride>>(
  (result, currentValue) => {
    result[`Mui${currentValue}`] = {
      defaultProps: {
        slots: {
          // openPickerIcon: calendarIcon,
          openPickerButton: calendarIcon,
          previousIconButton: leftIcon,
          nextIconButton: rightIcon,
          // switchViewIcon: switchIcon,
          switchViewButton: switchIcon,
        },
        slotProps: {
          textField: {
            InputLabelProps: { shrink: true },
          },
        },
      },
    };

    return result;
  },
  {}
);

const timeTypes = timeList.reduce<Record<string, PickerComponentOverride>>(
  (result, currentValue) => {
    result[`Mui${currentValue}`] = {
      defaultProps: {
        slots: {
          openPickerIcon: clockIcon,
          rightArrowIcon: rightIcon,
          switchViewIcon: switchIcon,
        },
      },
    };

    return result;
  },
  {}
);

export default function DatePicker(theme: Theme) {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          [`&.${inputBaseClasses.root}`]: {
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(0.5, 1),
          },
        },
        input: {
          fontSize: theme.typography.body3.fontSize,
          color: theme.palette.grey[700],
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: theme.spacing(0.5),
            borderColor: theme.palette.grey[400],
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        labelContainer: {
          fontSize: theme.typography.body3.fontSize,
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        spacer: {
          width: theme.spacing(1),
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          borderRadius: theme.spacing(0.5),
          '&:focus': {
            backgroundColor: `${theme.palette.primary[500]} !important`,
            color: theme.palette.common.white,
          },
        },
        today: {
          border: `1px solid ${theme.palette.primary[200]} !important`,
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          borderRadius: theme.spacing(0.5),
          fontSize: theme.typography.body3.fontSize,
          '&:focus': {
            backgroundColor: `${theme.palette.primary[500]} !important`,
          },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          borderRadius: theme.spacing(0.5),
          '&:focus': {
            backgroundColor: `${theme.palette.primary[500]} !important`,
          },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-actionBar': {
            [`& .${buttonClasses.root}:last-of-type`]: {
              backgroundColor: theme.palette.text.primary,
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.common.white
                  : theme.palette.grey[800],
            },
          },
        },
      },
    },

    // Date
    ...desktopTypes,

    // Time
    ...timeTypes,
  };
}
